import { useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { colorPalette } from "../styles/theme";

/**
 * 개인 vs 기관 평균 일별 추이 그래프 (참가자 관리 하단 3단 그래프, 샘플)
 * - views/UserDetailView.js의 DailyDualLineChart 구조(실선 개인 / 점선 기관 평균 / 검은 점선 지역 평균)를 유지하면서
 *   알고리즘 IP 보호를 위해 모든 소수점 수치를 숨기고 단계 텍스트로만 표시합니다.
 *   · Y축: 0~1 고정, 눈금 숫자 대신 [낮음]-[보통]-[높음]-[매우 높음] 구간 라벨
 *   · 기준선/범례/툴팁: 숫자 없이 "지역 평균 기준선", "기관 평균 추이", 단계명만 표기
 * - 디스트레스 그래프에는 개인 기준선(baseline_score)을 회색 반투명 밴드 + 점선으로 표시
 * - 에피소드 발생일 점은 기본 점의 1.5배 크기 + 빨간 테두리, 마우스 오버 시 한글 상태 요약 툴팁
 */

// 종합 디스트레스 4단계 — 배경 밴드 / Y축 라벨 / 범례 / 툴팁 단계명에 사용 (주간 요약 카드와 같은 경계)
export const DISTRESS_LEVELS = [
  { min: 0, max: 0.16, axis: "낮음", stage: "안정", legend: "🟢 정상(안정) 구간", color: "#22c55e" },
  { min: 0.16, max: 0.41, axis: "보통", stage: "주의", legend: "🟡 경미/주의 구간", color: "#facc15" },
  { min: 0.41, max: 0.6, axis: "높음", stage: "경고", legend: "🟠 중등도/경고 구간", color: "#f97316" },
  { min: 0.6, max: 1, axis: "매우 높음", stage: "위험", legend: "🔴 중증/위험 구간", color: "#ef4444" },
];

// 증상 부담·부정 정서 4구간 — 주간 요약 "주요 신호 요약" 카드와 같은 경계 (배경 밴드 없이 Y축 라벨만 사용)
export const SIGNAL_LEVELS = [
  { min: 0, max: 0.25, axis: "낮음", stage: "낮음" },
  { min: 0.25, max: 0.5, axis: "보통", stage: "보통" },
  { min: 0.5, max: 0.75, axis: "높음", stage: "높음" },
  { min: 0.75, max: 1, axis: "매우 높음", stage: "매우높음" },
];

const findLevel = (levels, value) =>
  typeof value === "number" ? levels.find((level) => value <= level.max) ?? levels[levels.length - 1] : null;

// 지역 평균 — 주간 단위로 바뀌는 기관 평균과 달리 항상 고정되는 기준값 (UserDetailView와 동일한 하드코딩 값, 화면에는 숫자 비노출)
export const REGION_AVERAGE = { symptomAvg: 0.64, distressAvg: 0.38 };

// 개인 기준선 밴드 폭(기준선 ±) — 이 범위 안이면 "평소 개인 범위"로 봄
const BASELINE_BAND = 0.06;
// 기관 평균과의 차이가 이 값 미만이면 "유사"로 봄 (주간 요약 카드와 같은 기준)
const SIMILAR_THRESHOLD = 0.05;

const POINT_RADIUS = 5;
const EPISODE_POINT_RADIUS = POINT_RADIUS * 1.5; // 에피소드 발생일은 기본 점의 1.5배

const EPISODE_COLOR = "#ef4444";
const BASELINE_COLOR = "#9ca3af";
const REGION_COLOR = "#111827";

// 날짜 라벨이 겹치지 않도록 데이터 포인트 하나당 확보할 최소 가로 간격(px)
const MIN_X_STEP = 42;
const MIN_CHART_WIDTH = 560;

/**
 * program-detail의 daily_scores를 그래프용 데이터로 변환 (반영된 기록 포함)
 * @param {string} [baselineKey] - 개인 기준선 필드명 (디스트레스 그래프만 "baseline_score" 전달)
 */
export const buildDailyDualSeries = (dailyScores, userKey, orgKey, baselineKey) =>
  (Array.isArray(dailyScores) ? dailyScores : []).map((d) => ({
    date: d.date,
    userValue: typeof d[userKey] === "number" ? d[userKey] : null,
    orgValue: typeof d[orgKey] === "number" ? d[orgKey] : null,
    baseline: baselineKey && typeof d[baselineKey] === "number" ? d[baselineKey] : null,
    records: d.records ?? null,
    isEpisode: Boolean(d.is_episode),
  }));

/** 좌표 배열을 SVG path로 변환 — 값이 없는(null) 날짜는 건너뛰고, 유효한 점들끼리는 계속 이어서 그림 */
const buildLinePath = (points) => {
  let path = "";
  let started = false;
  points.forEach((p) => {
    if (!p) return;
    path += `${started ? "L" : "M"} ${p.x} ${p.y} `;
    started = true;
  });
  return path.trim();
};

/** 반영된 기록 문구 — 예: "감정체크 완료, 챗봇 대화 완료" */
const formatRecords = (records) => {
  if (!records) return "-";
  const items = [records.emotion_check && "감정체크 완료", records.chatbot && "챗봇 대화 완료"].filter(Boolean);
  return items.length ? items.join(", ") : "-";
};

/** 툴팁 비교 문구 — 개인 기준선이 있으면 평소 개인 범위 대비, 없으면 기관 평균 대비 */
const describeComparison = (point) => {
  if (typeof point.baseline === "number") {
    if (point.userValue > point.baseline + BASELINE_BAND) return "평소 개인 범위 대비 상승";
    if (point.userValue < point.baseline - BASELINE_BAND) return "평소 개인 범위 대비 하강";
    return "평소 개인 범위 내";
  }
  if (typeof point.orgValue !== "number") return null;
  const diff = point.userValue - point.orgValue;
  if (Math.abs(diff) < SIMILAR_THRESHOLD) return "기관 평균과 유사";
  return `기관 평균 대비 ${diff > 0 ? "높음" : "낮음"}`;
};

/** 범례 한 항목 — 선/띠/점 모양 견본 + 라벨 */
const LegendItem = ({ swatch, label }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
    <Box component="svg" width={22} height={12} sx={{ display: "block", flexShrink: 0 }}>
      {swatch}
    </Box>
    <Typography sx={{ fontSize: 11, color: colorPalette.textSecondary, whiteSpace: "nowrap" }}>{label}</Typography>
  </Box>
);

/**
 * @param {Array} data - buildDailyDualSeries 결과
 * @param {string} color - 개인/기관 평균 선 색상
 * @param {Array} levels - 구간 정의 (DISTRESS_LEVELS면 배경 밴드 표시, SIGNAL_LEVELS면 라벨만)
 * @param {string} metricLabel - 툴팁 지표명 (예: "종합 디스트레스")
 * @param {boolean} stageSuffix - 툴팁 단계명 뒤에 "단계"를 붙일지 (디스트레스: "주의 단계")
 * @param {number} regionAvg - 지역 평균 기준선 위치 (화면에는 숫자 비노출)
 * @param {boolean} showBaseline - 개인 기준선 밴드 표시 여부
 */
const DailyDualLineChart = ({
  data,
  color,
  levels = SIGNAL_LEVELS,
  metricLabel,
  stageSuffix = false,
  regionAvg,
  showBaseline = false,
}) => {
  const height = 220;
  const padding = { top: 16, right: 16, bottom: 32, left: 58 };
  const [hoveredIndex, setHoveredIndex] = useState(null);

  if (!data || data.length === 0) {
    return (
      <Box
        sx={{
          py: 5,
          textAlign: "center",
          border: `1px dashed ${colorPalette.border}`,
          borderRadius: 1,
          color: colorPalette.textSecondary,
        }}
      >
        <Typography variant="body2">표시할 데이터가 없습니다.</Typography>
      </Box>
    );
  }

  // 데이터가 많아질수록(기간이 길수록) SVG 전체 너비를 늘려서 x축 라벨이 겹치지 않게 하고,
  // 바깥 Box의 overflowX:auto로 가로 스크롤이 생기도록 함
  const width = Math.max(MIN_CHART_WIDTH, padding.left + padding.right + (data.length - 1) * MIN_X_STEP);
  const innerWidth = width - padding.left - padding.right;
  const innerHeight = height - padding.top - padding.bottom;

  // 구간 라벨이 항상 같은 위치를 가리키도록 Y축은 0~1로 고정
  const xStep = data.length > 1 ? innerWidth / (data.length - 1) : 0;
  const xScale = (index) => padding.left + xStep * index;
  const yScale = (value) => padding.top + innerHeight - Math.min(Math.max(value, 0), 1) * innerHeight;

  const userPoints = data.map((d, i) => (typeof d.userValue === "number" ? { x: xScale(i), y: yScale(d.userValue) } : null));
  const orgPoints = data.map((d, i) => (typeof d.orgValue === "number" ? { x: xScale(i), y: yScale(d.orgValue) } : null));
  const hasBandColors = Boolean(levels[0].color);

  // 개인 기준선 밴드 (기준선 ±BASELINE_BAND) — 값이 있는 구간만 이어서 그림
  const baselinePoints = showBaseline
    ? data.map((d, i) => (typeof d.baseline === "number" ? { i, value: d.baseline } : null)).filter(Boolean)
    : [];
  const baselineBandPath =
    baselinePoints.length > 1
      ? [
          ...baselinePoints.map((p, k) => `${k === 0 ? "M" : "L"} ${xScale(p.i)} ${yScale(p.value + BASELINE_BAND)}`),
          ...[...baselinePoints].reverse().map((p) => `L ${xScale(p.i)} ${yScale(p.value - BASELINE_BAND)}`),
          "Z",
        ].join(" ")
      : "";
  const baselineLinePath = buildLinePath(baselinePoints.map((p) => ({ x: xScale(p.i), y: yScale(p.value) })));

  const hovered = hoveredIndex != null ? data[hoveredIndex] : null;
  const hoveredHasValue = hovered && typeof hovered.userValue === "number";

  return (
    <Box>
      {/* 선 종류 범례 — 숫자 없이 의미만 표기 */}
      <Box sx={{ display: "flex", flexWrap: "wrap", columnGap: 2, rowGap: 0.5, mb: 1 }}>
        <LegendItem swatch={<line x1="1" x2="21" y1="6" y2="6" stroke={color} strokeWidth="2.5" />} label="개인" />
        <LegendItem
          swatch={<line x1="1" x2="21" y1="6" y2="6" stroke={color} strokeOpacity="0.5" strokeWidth="2" strokeDasharray="5 4" />}
          label="기관 평균 추이"
        />
        {typeof regionAvg === "number" && (
          <LegendItem
            swatch={<line x1="1" x2="21" y1="6" y2="6" stroke={REGION_COLOR} strokeWidth="1.5" strokeDasharray="6 4" />}
            label="지역 평균 기준선"
          />
        )}
        {showBaseline && (
          <LegendItem
            swatch={
              <>
                <rect x="1" y="2" width="20" height="8" fill={BASELINE_COLOR} fillOpacity="0.2" />
                <line x1="1" x2="21" y1="6" y2="6" stroke={BASELINE_COLOR} strokeWidth="1.5" strokeDasharray="3 3" />
              </>
            }
            label="평소 개인 범위(기준선)"
          />
        )}
        <LegendItem
          swatch={<circle cx="11" cy="6" r="4.5" fill={color} stroke={EPISODE_COLOR} strokeWidth="2" />}
          label="에피소드(급성 악화) 발생일"
        />
      </Box>

      <Box sx={{ position: "relative", overflowX: "auto" }}>
        <svg width={width} height={height} role="img" style={{ display: "block" }}>
          {/* 위험 단계 배경 밴드 (디스트레스만) */}
          {hasBandColors &&
            levels.map((level) => (
              <rect
                key={level.axis}
                x={padding.left}
                y={yScale(level.max)}
                width={innerWidth}
                height={Math.max(yScale(level.min) - yScale(level.max), 0)}
                fill={level.color}
                fillOpacity="0.1"
              />
            ))}

          {/* 구간 경계 그리드 + Y축 구간 라벨 (소수점 눈금 대신 낮음~매우 높음) */}
          {[0, ...levels.map((level) => level.max)].map((boundary) => (
            <line
              key={boundary}
              x1={padding.left}
              x2={width - padding.right}
              y1={yScale(boundary)}
              y2={yScale(boundary)}
              stroke={colorPalette.border}
              strokeDasharray={boundary === 0 ? undefined : "4 4"}
            />
          ))}
          {levels.map((level) => (
            <text
              key={level.axis}
              x={padding.left - 8}
              y={yScale((level.min + level.max) / 2) + 3}
              textAnchor="end"
              fontSize="10"
              fill={colorPalette.textSecondary}
            >
              {level.axis}
            </text>
          ))}

          {/* 개인 기준선 밴드 + 점선 (수치 비노출) */}
          {baselineBandPath && <path d={baselineBandPath} fill={BASELINE_COLOR} fillOpacity="0.18" stroke="none" />}
          {baselineLinePath && (
            <path d={baselineLinePath} fill="none" stroke={BASELINE_COLOR} strokeWidth="1.5" strokeDasharray="3 3" />
          )}

          {/* 지역 평균 기준선 — 주간 변동 없는 고정값, 라벨에 숫자 비노출 */}
          {typeof regionAvg === "number" && (
            <g>
              <line
                x1={padding.left}
                x2={width - padding.right}
                y1={yScale(regionAvg)}
                y2={yScale(regionAvg)}
                stroke={REGION_COLOR}
                strokeWidth="1.5"
                strokeDasharray="6 4"
              />
              {/* 최근 날짜(오른쪽 끝) 점과 겹치지 않도록 라벨은 왼쪽 시작점에 표시 */}
              <text x={padding.left + 6} y={yScale(regionAvg) - 5} textAnchor="start" fontSize="10" fontWeight="700" fill={REGION_COLOR}>
                지역 평균 기준선
              </text>
            </g>
          )}

          {/* 기관 평균 추이 (점선) */}
          <path d={buildLinePath(orgPoints)} fill="none" stroke={color} strokeOpacity="0.45" strokeWidth="2" strokeDasharray="5 4" />
          {/* 개인선 (실선) */}
          <path d={buildLinePath(userPoints)} fill="none" stroke={color} strokeWidth="2.5" />

          {/* 호버한 날짜 세로 가이드 */}
          {hovered && (
            <line
              x1={xScale(hoveredIndex)}
              x2={xScale(hoveredIndex)}
              y1={padding.top}
              y2={padding.top + innerHeight}
              stroke={colorPalette.textSecondary}
              strokeOpacity="0.35"
            />
          )}

          {data.map((d, i) => {
            const point = userPoints[i];
            const level = hasBandColors ? findLevel(levels, d.userValue) : null;
            const baseRadius = d.isEpisode ? EPISODE_POINT_RADIUS : POINT_RADIUS;
            return (
              <g key={d.date ?? i}>
                {/* 기관 평균 점 */}
                {orgPoints[i] && (
                  <circle cx={orgPoints[i].x} cy={orgPoints[i].y} r="4" fill={color} fillOpacity="0.35" style={{ pointerEvents: "none" }} />
                )}

                {/* 개인 점 — 디스트레스는 단계 색상, 에피소드 발생일은 1.5배 크기 + 빨간 테두리 */}
                {point && (
                  <circle
                    cx={point.x}
                    cy={point.y}
                    r={hoveredIndex === i ? baseRadius + 1 : baseRadius}
                    fill={level?.color ?? color}
                    stroke={d.isEpisode ? EPISODE_COLOR : "#fff"}
                    strokeWidth={d.isEpisode ? 2.5 : 1.5}
                    style={{ pointerEvents: "none", transition: "r 0.1s ease" }}
                  />
                )}

                <text x={xScale(i)} y={height - padding.bottom + 16} textAnchor="middle" fontSize="10" fill={colorPalette.textSecondary}>
                  {d.date ? d.date.slice(5) : ""}
                </text>

                {/* 히트 영역 (마우스오버 감지용, 화면에 보이지 않음) — 날짜 칸 전체 */}
                <rect
                  x={xScale(i) - Math.max(xStep, 16) / 2}
                  y={padding.top}
                  width={Math.max(xStep, 16)}
                  height={innerHeight}
                  fill="transparent"
                  onMouseEnter={() => setHoveredIndex(i)}
                  onMouseLeave={() => setHoveredIndex((prev) => (prev === i ? null : prev))}
                  style={{ cursor: "pointer" }}
                />
              </g>
            );
          })}
        </svg>

        {/* 한글 상태 요약 툴팁 — 숫자 없이 단계/비교/기록/에피소드 여부만 표시 */}
        {hovered && (() => {
          const tooltipLeft = xScale(hoveredIndex);
          const alignRight = tooltipLeft > width / 2;
          const stage = findLevel(levels, hovered.userValue)?.stage;
          const comparison = hoveredHasValue ? describeComparison(hovered) : null;
          const previous = hoveredIndex > 0 ? data[hoveredIndex - 1] : null;
          const rising = typeof previous?.userValue === "number" && hovered.userValue > previous.userValue;
          return (
            <Box
              sx={{
                position: "absolute",
                left: tooltipLeft,
                top: padding.top,
                transform: `translateX(${alignRight ? "calc(-100% - 12px)" : "12px"})`,
                px: 1.5,
                py: 1,
                borderRadius: 1,
                backgroundColor: "#1a1f36",
                color: "#fff",
                fontSize: 12,
                whiteSpace: "nowrap",
                pointerEvents: "none",
                boxShadow: "0 4px 12px rgba(0,0,0,0.18)",
                zIndex: 1,
                lineHeight: 1.7,
              }}
            >
              <Box sx={{ fontWeight: 700, mb: 0.25 }}>[{hovered.date} 상태 요약]</Box>
              {hoveredHasValue ? (
                <>
                  <Box>
                    • {metricLabel}: {stage}
                    {stageSuffix ? " 단계" : ""}
                    {comparison ? ` (${comparison})` : ""}
                  </Box>
                  <Box>• 반영된 기록: {formatRecords(hovered.records)}</Box>
                  <Box sx={{ color: hovered.isEpisode ? "#fca5a5" : "inherit", fontWeight: hovered.isEpisode ? 700 : 400 }}>
                    • 에피소드 여부:{" "}
                    {hovered.isEpisode ? `⚡ 급성 악화 감지${rising ? " (연속 상승)" : ""}` : "없음"}
                  </Box>
                </>
              ) : (
                <Box>• 이날은 기록이 없습니다.</Box>
              )}
            </Box>
          );
        })()}
      </Box>
    </Box>
  );
};

/** 디스트레스 4단계 구간 범례 — 내부 절단점 수치 비노출 */
export const DistressLevelLegend = () => (
  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 1 }}>
    {DISTRESS_LEVELS.map((level) => (
      <Typography key={level.legend} variant="caption" sx={{ color: colorPalette.textSecondary }}>
        {level.legend}
      </Typography>
    ))}
  </Box>
);

export default DailyDualLineChart;
